// Camada de persistência do modo local (sem conta, sem servidor).
//
// Escrita à mão sobre a API nativa em vez de usar o pacote `idb`: a superfície
// aqui é um único object store de chave/valor, e adicionar dependência neste
// projeto exige regenerar o package-lock no Linux (ver ESTADO.md). `idb` é
// açúcar que não pagaria esse custo.

export interface Backend {
  read<T>(key: string): Promise<T | undefined>;
  write<T>(key: string, value: T): Promise<void>;
  remove(key: string): Promise<void>;
}

const DB_NAME = 'balancedsquad';
const DB_VERSION = 1;
const STORE = 'app';

// Guarda em memória. Serve para os testes e como rede de segurança quando o
// IndexedDB não está disponível — navegação privada, cookies bloqueados, iframe
// de terceiro. O app continua funcionando na sessão; só não sobrevive ao
// recarregamento, que é melhor do que uma tela branca.
export function memoryBackend(): Backend {
  const map = new Map<string, unknown>();
  return {
    read: <T>(key: string) => Promise.resolve(map.get(key) as T | undefined),
    write: (key, value) => {
      map.set(key, value);
      return Promise.resolve();
    },
    remove: (key) => {
      map.delete(key);
      return Promise.resolve();
    },
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB indisponível'));
    // Dispara quando outra aba segura uma versão antiga do banco. Sem isto a
    // promessa ficaria pendurada para sempre e a tela nunca carregaria.
    request.onblocked = () => reject(new Error('IndexedDB bloqueado por outra aba'));
  });
}

function runTransaction<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = run(tx.objectStore(STORE));
    request.onsuccess = () => resolve(request.result as T);
    request.onerror = () => reject(request.error ?? new Error('falha na transação'));
  });
}

export function indexedDbBackend(): Backend {
  // Uma conexão só, aberta na primeira operação. Se a abertura falhar, cai para
  // memória de uma vez e não tenta de novo a cada leitura.
  let connection: Promise<Backend> | null = null;

  function resolveBackend(): Promise<Backend> {
    connection ??= openDatabase()
      .then((db): Backend => ({
        read: (key) => runTransaction(db, 'readonly', (store) => store.get(key)),
        write: (key, value) =>
          runTransaction<void>(db, 'readwrite', (store) => store.put(value, key)),
        remove: (key) => runTransaction<void>(db, 'readwrite', (store) => store.delete(key)),
      }))
      .catch(() => memoryBackend());
    return connection;
  }

  return {
    read: async <T>(key: string) => (await resolveBackend()).read<T>(key),
    write: async (key, value) => (await resolveBackend()).write(key, value),
    remove: async (key) => (await resolveBackend()).remove(key),
  };
}
